import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CreativePerformanceService from 'services/CreativePerformanceService';
import SettingsService from 'services/SettingsService';

import {
  ANGLE_OPTIONS,
  HEADLINE_ANGLE_OPTIONS,
  STYLE_ANGLE_CONFLICTS,
  coerceModelOption,
  composePrompt,
  DEFAULT_STUDIO_PROMPT,
  estimateTokens,
  loadParams,
  MODEL_OPTIONS,
  saveParams,
  buildStyleLibrary,
  DEFAULT_FORMAT_RULES,
  DEFAULT_HEADLINE_RULES,
  splitSuggestions,
  STYLE_LIBRARY,
  STUDIO_SECTION_GROUPS,
  STUDIO_SECTIONS,
  StudioSectionGroup,
  STUDIO_FORMAT_RULES_KEY,
  STUDIO_FORMAT_RULES_STORAGE_KEY,
  STUDIO_HEADLINE_RULES_KEY,
  STUDIO_HEADLINE_RULES_STORAGE_KEY,
  SuggestionOption,
  STUDIO_MODEL_STORAGE_KEY,
  STUDIO_PARAMS_KEY,
  STUDIO_PARAMS_STORAGE_KEY,
  STUDIO_PROMPT_KEY,
  STUDIO_PROMPT_STORAGE_KEY,
  STUDIO_SETTINGS_NAMESPACE,
  StudioParams,
  VAR_ANALYSIS,
  VAR_EXTRA,
  VAR_MODELED_COUNT,
  VAR_NOVEL_COUNT,
  VAR_ORIGINAL_HEADLINE,
  VAR_ORIGINAL_PRIMARY_TEXT,
  VAR_FORMAT_RULES,
  VAR_HEADLINE_RULES,
  VAR_PREVIOUS_COPY,
  VAR_STYLE_LIBRARY,
  VAR_PRODUCT,
  VAR_SELLING_POINTS,
  VAR_WINNING_HEADLINES,
  VAR_WINNING_PRIMARY_TEXTS,
} from './studioSettings';

// The Ad Copy Studio: winners-grounded, fully inspectable copy generation.
//
// Every input is an INGREDIENT CARD, not hidden plumbing: toggle it, check
// individual items, edit the exact text that will be injected, and watch the
// token gauge. Winners come from our own ads' asset-level performance
// (creative-performance /assets — real spend and ROAS per exact wording).
// Host-adapted: the static-creative pipeline is the first host; the video
// editor adopts it once proven.

export interface StudioHostData {
  product?: string | null;
  sellingPoints?: string[];
  /** Pre-check state per selling point (e.g. the run's chosen subset);
   * defaults to all checked when absent. */
  sellingPointsChecked?: boolean[];
  analysis?: string | null;
  originalHeadline?: string | null;
  originalPrimaryText?: string | null;
}

/** A persisted past run — the host stores each generation keyed by its own
 * unique id (static output id now, video script id later) so history survives
 * closing the dialog. */
export interface StudioRun {
  runId: string;
  model: string | null;
  responseText: string | null;
  createdAt: string;
  /** The host surface the run came from (e.g. which output of the ad). */
  hostId?: string | null;
}

interface AdCopyStudioProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Resolves the host's distilled inputs when the dialog opens. */
  loadHostData: () => Promise<StudioHostData>;
  /** Past runs for this creative, newest first. Absent = no persistence. */
  loadHistory?: () => Promise<StudioRun[]>;
  /** Raw response texts of past runs for the SAME PRODUCT (across creatives) —
   * parsed into the anti-repetition ingredient. */
  loadPriorCopy?: () => Promise<string[]>;
  /** Permanently removes a stored run. Absent = runs aren't deletable. */
  deleteRun?: (runId: string) => Promise<void>;
  /** How many of each kind the host's panel holds — the studio confirms and
   * applies exactly this many, so the count it names is the count that lands. */
  maxItems: number;
  generate: (prompt: string, model: string) => Promise<{
    text: string; model?: string; runId?: string; truncated?: boolean;
    /** Set when the copy was generated (and paid for) but could not be written
     * to the run history — the copy is fine, the history entry is missing. */
    historyError?: string | null;
  }>;
  /** Return false when the host can't take another item (all slots full). */
  onUseHeadline: (text: string) => boolean;
  onUsePrimaryText: (text: string) => boolean;
  /** Called right after a successful generation with every option (winners
   * first) — the host fills its slots so the user edits from there. The
   * explicit "Use All" action passes save: true so the host persists too. */
  onApplyAll?: (headlines: string[], primaryTexts: string[], opts?: { save?: boolean }) => void;
  /** Current slot contents, for the pick-one-to-replace dialog when full. */
  getSlots?: (kind: 'headline' | 'primaryText') => string[];
  replaceSlot?: (kind: 'headline' | 'primaryText', index: number, text: string) => void;
}

interface WinnerRow {
  text: string;
  spend: number;
  metaRoas: number | null;
  trueRoas: number | null;
  checked: boolean;
}

// Sample mode: the server returns at most this many rows, so the ROAS floor
// can only mean "everything above the floor" if that cut is RANKED on the same
// measure the floor reads. Ranking by spend (or any non-ROAS metric) and then
// filtering by ROAS in the browser yields "the top spenders that happen to
// clear the floor" instead, which is not what the control says.
const SAMPLE_POOL_LIMIT = 50;
const SAMPLE_SORT = 'true_roas';
/** The ROAS the rows display and the floor measures — kept identical so the
 * pool cut, the floor and the on-screen number can never disagree. */
const rowRoas = (r: WinnerRow) => r.trueRoas ?? r.metaRoas;

// The winner tunables are free-typed numbers; without this every keystroke
// fires a query (and a late one could land on top of a newer one).
const WINNERS_DEBOUNCE_MS = 400;

function drawSample<T>(pool: T[], n: number): T[] {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const ratio = (n: number | null) => (n == null ? '—' : `${n.toFixed(2)}x`);

// Headlines are single-line, so one quoted bullet per line stays unambiguous.
const winnerLines = (rows: WinnerRow[]) => rows
  .filter(r => r.checked)
  .map(r => `- "${r.text}" — ${ratio(rowRoas(r))} ROAS, ${money(r.spend)} spend`)
  .join('\n');

// Multi-line texts (primary copy) can't be bulleted — real ad copy contains
// hyphen bullets, quotes and blank lines, any of which would read as an item
// boundary and corrupt the pattern the model is meant to study. Number each
// item and fence its text in """ so everything inside is unambiguously
// content; the header line scopes metadata to the whole item.
const fencedBlocks = (texts: string[], header: (i: number) => string = (i) => `${i + 1}.`) => texts
  .map((text, i) => `${header(i)}\n"""\n${text}\n"""`)
  .join('\n\n');

const fencedWinnerBlocks = (rows: WinnerRow[]) => {
  const picked = rows.filter(r => r.checked);
  return fencedBlocks(
    picked.map(r => r.text),
    i => `WINNER ${i + 1} — ${ratio(rowRoas(picked[i]))} ROAS, ${money(picked[i].spend)} spend:`,
  );
};
const isoDaysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);

function IngredientCard({ id, label, enabled, onToggle, tokens, children }: {
  id: string; label: string; enabled: boolean; onToggle: (on: boolean) => void;
  tokens: number; children: React.ReactNode;
}) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, opacity: enabled ? 1 : 0.55 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Switch size="small" checked={enabled} onChange={e => onToggle(e.target.checked)} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{label}</Typography>
        <Tooltip title="Approximate tokens this card adds to the prompt">
          <Chip size="small" variant="outlined" label={enabled ? `~${tokens} tok` : 'off'} sx={{ height: 18, fontSize: '0.6rem' }} />
        </Tooltip>
      </Stack>
      {enabled && <Box sx={{ mt: 0.5 }}>{children}</Box>}
    </Box>
  );
}

function CheckList({ items, onToggle, meta }: {
  items: { text: string; checked: boolean }[];
  onToggle: (index: number) => void;
  meta?: (index: number) => string | null;
}) {
  return (
    <Stack spacing={0.25}>
      {items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Stack key={i} direction="row" alignItems="flex-start" spacing={0.5}>
          <Checkbox size="small" checked={item.checked} onChange={() => onToggle(i)} sx={{ p: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{item.text}</Typography>
            {meta?.(i) && <Typography variant="caption" color="text.secondary">{meta(i)}</Typography>}
          </Box>
        </Stack>
      ))}
      {!items.length && <Typography variant="caption" color="text.disabled">Nothing available.</Typography>}
    </Stack>
  );
}

interface TaggedOption { text: string; tag: 'winners' | 'novel'; angle?: string; style?: string; truncated?: boolean; }

/** The first non-empty line of a primary text — its hook — clipped at a word
 * boundary. The anti-repetition list carries only this much of each prior. */
function firstHookLine(text: string): string {
  const line = (text || '').split('\n').map(l => l.trim()).find(Boolean) || '';
  if (line.length <= 90) return line;
  const cut = line.slice(0, 90);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 60))}…`;
}

/** Fisher–Yates — Math.random is fine here; the draw only feeds prompt variety. */
function shuffled<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// One flat list per copy type (headlines / primary texts) — Meta couples them
// automatically at delivery, so they are independent here. Each row carries a
// winners/novel tag, an editable draft, and a Use button with click feedback —
// the target panel sits behind this modal, so an unacknowledged click reads
// as "nothing happened".
function SuggestionList({ title, options, onUse, onFull, onTextsChange }: {
  title: string;
  options: TaggedOption[];
  onUse: (text: string) => boolean;
  /** All slots taken — the studio opens the pick-one-to-replace dialog. */
  onFull: (text: string) => void;
  /** Reports the on-screen texts (drafts included) for "Replace all". */
  onTextsChange: (texts: string[]) => void;
}) {
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [status, setStatus] = useState<Record<number, 'added'>>({});
  useEffect(() => { setDrafts({}); setStatus({}); }, [options]);
  useEffect(() => {
    onTextsChange(options.map((o, i) => (drafts[i] ?? o.text).trim()).filter(Boolean));
  }, [drafts, options, onTextsChange]);
  if (!options.length) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      <Stack spacing={0.75}>
        {options.map((option, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
            <Stack spacing={0.4} sx={{ mt: 0.75, minWidth: 96, alignItems: 'flex-start' }}>
              <Chip
                size="small"
                label={option.tag === 'winners' ? 'winners' : 'novel'}
                color={option.tag === 'winners' ? 'primary' : 'secondary'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.62rem' }}
              />
              {option.truncated && (
                <Tooltip title="The response ran out of room inside this text — it stops mid-sentence. Finish it before using it.">
                  <Chip size="small" color="warning" label="cut off" sx={{ height: 18, fontSize: '0.58rem' }} />
                </Tooltip>
              )}
              {option.style && (
                <Tooltip title="Caption format — each option tests a different one">
                  <Chip size="small" variant="outlined" label={option.style} sx={{ height: 18, fontSize: '0.58rem' }} />
                </Tooltip>
              )}
              {option.angle && (
                <Tooltip title="Messaging angle">
                  <Chip size="small" variant="outlined" color="default" label={option.angle} sx={{ height: 18, fontSize: '0.58rem' }} />
                </Tooltip>
              )}
            </Stack>
            <TextField
              size="small" fullWidth multiline maxRows={10}
              value={drafts[i] ?? option.text}
              onChange={e => setDrafts(prev => ({ ...prev, [i]: e.target.value }))}
            />
            <Button
              size="small"
              color={status[i] === 'added' ? 'success' : 'primary'}
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => {
                const value = (drafts[i] ?? option.text).trim();
                if (!onUse(value)) {
                  onFull(value);
                  return;
                }
                setStatus(prev => ({ ...prev, [i]: 'added' }));
                setTimeout(() => setStatus(prev => {
                  const next = { ...prev };
                  delete next[i];
                  return next;
                }), 2000);
              }}
            >
              {status[i] === 'added' ? 'Added ✓' : 'Use'}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export default function AdCopyStudio({
  open, onClose, title, maxItems, loadHostData, loadHistory, loadPriorCopy, deleteRun, generate, onUseHeadline, onUsePrimaryText, onApplyAll, getSlots, replaceSlot,
}: AdCopyStudioProps) {
  const perfService = useMemo(() => new CreativePerformanceService(), []);
  const settingsService = useMemo(() => new SettingsService(), []);

  const [params, setParams] = useState<StudioParams>(() => loadParams());
  const [template, setTemplate] = useState(() => localStorage.getItem(STUDIO_PROMPT_STORAGE_KEY) || DEFAULT_STUDIO_PROMPT);
  const [headlineRules, setHeadlineRules] = useState(() => localStorage.getItem(STUDIO_HEADLINE_RULES_STORAGE_KEY) || DEFAULT_HEADLINE_RULES);
  const [formatRules, setFormatRules] = useState(() => localStorage.getItem(STUDIO_FORMAT_RULES_STORAGE_KEY) || DEFAULT_FORMAT_RULES);
  const [model, setModel] = useState(() => coerceModelOption(localStorage.getItem(STUDIO_MODEL_STORAGE_KEY) || undefined));

  const [host, setHost] = useState<StudioHostData | null>(null);
  const [hostLoading, setHostLoading] = useState(false);
  const [winningHeadlines, setWinningHeadlines] = useState<WinnerRow[]>([]);
  const [winningPrimary, setWinningPrimary] = useState<WinnerRow[]>([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  // A failed winners load must never look like "no winners": the run would be
  // ungrounded while the panel still claims it is winners-grounded — and the
  // operator pays for it either way.
  const [winnersError, setWinnersError] = useState<string | null>(null);
  const [winnersReloadNonce, setWinnersReloadNonce] = useState(0);
  // Only the newest winners request may write state — the tunables are typed,
  // so an earlier (slower) response can land after a newer one.
  const winnersRequest = useRef(0);
  // Full above-floor pools kept for re-drawing in sample mode.
  const winnersPool = useRef<{ h: WinnerRow[]; p: WinnerRow[] }>({ h: [], p: [] });
  const [priorItems, setPriorItems] = useState<{ text: string; kind: 'headline' | 'primary'; checked: boolean }[]>([]);
  const [sellingChecked, setSellingChecked] = useState<boolean[]>([]);
  const [analysisText, setAnalysisText] = useState('');
  const [originalText, setOriginalText] = useState({ headline: '', primaryText: '' });
  const [extra, setExtra] = useState('');

  const [generating, setGenerating] = useState(false);
  const [rawResponse, setRawResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Something the operator must know about a run that otherwise SUCCEEDED —
  // separate from `error`, which means the run produced nothing.
  const [warning, setWarning] = useState<string | null>(null);
  const [teamSavedFlash, setTeamSavedFlash] = useState(false);
  const [history, setHistory] = useState<StudioRun[]>([]);
  const [viewedRunId, setViewedRunId] = useState<string | null>(null);
  // Two panels: set up a generation vs. browse/select results. Results wins
  // as the landing tab whenever stored runs exist.
  const [tab, setTab] = useState<'generate' | 'results'>('generate');
  // The Generate panel is a two-step flow — assemble ingredients first, then a
  // focused generation step (counts, model, prompt). Both steps stay mounted.
  const [genStep, setGenStep] = useState<'ingredients' | 'generate'>('ingredients');
  // Whether the template editor is unfolded beneath the assembled-prompt view.
  const [templateOpen, setTemplateOpen] = useState(false);
  // Which ingredient GROUP is visible on the Generate panel; panels stay
  // mounted (CSS-hidden) so checkbox/edit state survives switching.
  const [ingredientTab, setIngredientTab] = useState<StudioSectionGroup>('creative');
  const [sectionSettingsOpen, setSectionSettingsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<StudioRun | null>(null);
  const [deletingRun, setDeletingRun] = useState(false);
  // Full-slots flow: which text is waiting for a slot to replace.
  const [replaceTarget, setReplaceTarget] = useState<{ kind: 'headline' | 'primaryText'; text: string } | null>(null);
  const [replaceAllOpen, setReplaceAllOpen] = useState(false);
  // On-screen texts (drafts included) reported by the two lists — what
  // "Replace all" writes into the panel.
  const [screenHeadlines, setScreenHeadlines] = useState<string[]>([]);
  const [screenPrimaries, setScreenPrimaries] = useState<string[]>([]);
  const onHeadlineTexts = useCallback((texts: string[]) => setScreenHeadlines(texts), []);
  const onPrimaryTexts = useCallback((texts: string[]) => setScreenPrimaries(texts), []);
  // What "Use All" actually writes — the panel holds maxItems of each, so the
  // confirmation must name these counts, not the on-screen ones.
  const applyHeadlines = screenHeadlines.slice(0, maxItems);
  const applyPrimaries = screenPrimaries.slice(0, maxItems);

  const setParam = <K extends keyof StudioParams>(key: K, value: StudioParams[K]) => {
    setParams(prev => {
      const next = { ...prev, [key]: value };
      saveParams(next);
      return next;
    });
  };
  const setEnabled = (id: string, on: boolean) => {
    setParams(prev => {
      const next = { ...prev, enabled: { ...prev.enabled, [id]: on } };
      saveParams(next);
      return next;
    });
  };

  // Team defaults (template + params) — applied only where the user has no
  // local edits, mirroring the ad-copy panel convention. If the read fails,
  // saving is disabled: writing now could overwrite team defaults we never saw.
  const teamLoaded = useRef(false);
  const [teamDefaultsError, setTeamDefaultsError] = useState(false);
  useEffect(() => {
    if (!open || teamLoaded.current) return;
    teamLoaded.current = true;
    void settingsService.getOrThrow(STUDIO_SETTINGS_NAMESPACE).then((resp) => {
      const teamPrompt = resp?.[STUDIO_PROMPT_KEY];
      if (typeof teamPrompt === 'string' && teamPrompt.trim() && !localStorage.getItem(STUDIO_PROMPT_STORAGE_KEY)) {
        setTemplate(teamPrompt);
      }
      const teamHeadlineRules = resp?.[STUDIO_HEADLINE_RULES_KEY];
      if (typeof teamHeadlineRules === 'string' && teamHeadlineRules.trim() && !localStorage.getItem(STUDIO_HEADLINE_RULES_STORAGE_KEY)) {
        setHeadlineRules(teamHeadlineRules);
      }
      const teamFormatRules = resp?.[STUDIO_FORMAT_RULES_KEY];
      if (typeof teamFormatRules === 'string' && teamFormatRules.trim() && !localStorage.getItem(STUDIO_FORMAT_RULES_STORAGE_KEY)) {
        setFormatRules(teamFormatRules);
      }
      const teamParams = resp?.[STUDIO_PARAMS_KEY];
      if (teamParams && typeof teamParams === 'object' && !localStorage.getItem(STUDIO_PARAMS_STORAGE_KEY)) {
        setParams(prev => ({ ...prev, ...(teamParams as Partial<StudioParams>) }));
      }
      setTeamDefaultsError(false);
    }).catch(() => setTeamDefaultsError(true));
  }, [open, settingsService]);

  // Host callbacks pinned to refs: the host re-creates them on unrelated
  // re-renders (the detail's list poller refreshes its row object every few
  // seconds), and keying the load effect on their identity made the whole
  // dialog reload — spinner, state reset — mid-interaction. The effect runs
  // on open only; the refs always hold the latest closures.
  const loadHostDataRef = useRef(loadHostData);
  const loadHistoryRef = useRef(loadHistory);
  const loadPriorCopyRef = useRef(loadPriorCopy);
  useEffect(() => {
    loadHostDataRef.current = loadHostData;
    loadHistoryRef.current = loadHistory;
    loadPriorCopyRef.current = loadPriorCopy;
  });

  // Host data on open.
  const openedFor = useRef(0);
  useEffect(() => {
    if (!open) return;
    openedFor.current += 1;
    const token = openedFor.current;
    setHostLoading(true);
    setRawResponse(null);
    setError(null);
    setWarning(null);
    // Direction typed for the LAST creative must not ride along into this one.
    setExtra('');
    void loadHostDataRef.current().then((data) => {
      if (openedFor.current !== token) return;
      setHost(data);
      setSellingChecked(
        data.sellingPointsChecked && data.sellingPointsChecked.length === (data.sellingPoints || []).length
          ? data.sellingPointsChecked
          : (data.sellingPoints || []).map(() => true),
      );
      setAnalysisText(data.analysis || '');
      setOriginalText({ headline: data.originalHeadline || '', primaryText: data.originalPrimaryText || '' });
    }).catch((err) => setError(err?.message || 'Failed to load inputs'))
      .finally(() => { if (openedFor.current === token) setHostLoading(false); });
    // Past runs load alongside — and the most recent one shows immediately,
    // so reopening a creative picks up where the last session left off.
    setHistory([]);
    setViewedRunId(null);
    setTab('generate');
    setGenStep('ingredients');
    setRotationNonce(nonce => nonce + 1);
    setPriorItems([]);
    if (loadPriorCopyRef.current) {
      void loadPriorCopyRef.current().then((raws) => {
        if (openedFor.current !== token) return;
        const seen = new Set<string>();
        const items: { text: string; kind: 'headline' | 'primary'; checked: boolean }[] = [];
        for (const raw of raws) {
          const parsed = splitSuggestions(raw);
          for (const o of [...parsed.headlinesModeled, ...parsed.headlinesNovel]) {
            const key = `h:${o.text.toLowerCase()}`;
            if (!seen.has(key)) { seen.add(key); items.push({ text: o.text, kind: 'headline', checked: true }); }
          }
          for (const o of [...parsed.primaryModeled, ...parsed.primaryNovel]) {
            const key = `p:${o.text.toLowerCase()}`;
            if (!seen.has(key)) { seen.add(key); items.push({ text: o.text, kind: 'primary', checked: true }); }
          }
        }
        // Cap so the card can't swamp the prompt (25 headlines, 15 primaries),
        // and auto-check only the 5 MOST RECENT of each kind — the rest stay
        // listed but unchecked, one click away.
        const heads = items.filter(i => i.kind === 'headline').slice(0, 25)
          .map((it, idx) => ({ ...it, checked: idx < 5 }));
        const prims = items.filter(i => i.kind === 'primary').slice(0, 15)
          .map((it, idx) => ({ ...it, checked: idx < 5 }));
        setPriorItems([...heads, ...prims]);
      }).catch(() => {});
    }
    if (loadHistoryRef.current) {
      void loadHistoryRef.current().then((runs) => {
        if (openedFor.current !== token) return;
        setHistory(runs);
        if (runs.length && runs[0].responseText) {
          setRawResponse(runs[0].responseText);
          setViewedRunId(runs[0].runId);
          setTab('results');
        }
      }).catch(() => {});
    }
  }, [open]);

  // Winners whenever the dialog is open and the winner tunables change.
  useEffect(() => {
    if (!open) return undefined;
    setWinnersLoading(true);
    const token = winnersRequest.current + 1;
    winnersRequest.current = token;
    const sample = params.winnersMode === 'sample';
    const timer = setTimeout(() => {
      const query = (assetType: 'headline' | 'primary_text') => perfService.getAssets({
        start: isoDaysAgo(params.lookbackDays),
        end: isoDaysAgo(0),
        assetType,
        minSpend: params.minSpend,
        // Sample mode's cut has to be ranked on the measure its floor reads.
        sort: sample ? SAMPLE_SORT : params.metric,
        direction: 'desc',
        limit: sample ? SAMPLE_POOL_LIMIT : params.winnersPerType,
      } as any);
      void Promise.all([query('headline'), query('primary_text')]).then(([h, p]) => {
        if (winnersRequest.current !== token) return;
        // BaseService logs the real error and resolves null. Empty rows and a
        // failed request are different facts and must read differently.
        if (!h || !p) {
          winnersPool.current = { h: [], p: [] };
          setWinningHeadlines([]);
          setWinningPrimary([]);
          setWinnersError("Winners couldn't be loaded (the request failed — details are in the browser console).");
          return;
        }
        setWinnersError(null);
        const toRows = (resp: any): WinnerRow[] => ((resp?.rows || resp?.assets || []) as any[])
          .filter(r => r.asset_text)
          .map(r => ({
            text: String(r.asset_text),
            spend: Number(r.spend) || 0,
            metaRoas: r.meta_roas != null ? Number(r.meta_roas) : null,
            trueRoas: r.true_roas != null ? Number(r.true_roas) : null,
            checked: true,
          }));
        if (sample) {
          // Everything above the ROAS floor qualifies; a random N of them feed
          // the prompt, so each draw grounds the model differently.
          const floor = (r: WinnerRow) => (rowRoas(r) ?? 0) >= params.roasFloor;
          winnersPool.current = { h: toRows(h).filter(floor), p: toRows(p).filter(floor) };
          setWinningHeadlines(drawSample(winnersPool.current.h, params.winnersPerType));
          setWinningPrimary(drawSample(winnersPool.current.p, params.winnersPerType));
        } else {
          winnersPool.current = { h: [], p: [] };
          setWinningHeadlines(toRows(h));
          setWinningPrimary(toRows(p));
        }
      }).catch((err: any) => {
        if (winnersRequest.current !== token) return;
        winnersPool.current = { h: [], p: [] };
        setWinningHeadlines([]);
        setWinningPrimary([]);
        setWinnersError(err?.message || "Winners couldn't be loaded.");
      }).finally(() => {
        if (winnersRequest.current === token) setWinnersLoading(false);
      });
    }, WINNERS_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, params.lookbackDays, params.minSpend, params.metric, params.winnersPerType, params.winnersMode, params.roasFloor, winnersReloadNonce, perfService]);

  // Left to its own judgment the model repeats its favorite style/angle
  // pairings run after run, so each run gets a fresh random rotation the
  // prompt tells it to follow exactly. The nonce redraws it per run/open;
  // between those the draw is stable so the assembled-prompt view doesn't
  // churn on unrelated re-renders.
  const [rotationNonce, setRotationNonce] = useState(0);
  const rotationText = useMemo(() => {
    const styles = shuffled(STYLE_LIBRARY.filter(s => params.styles[s.key] !== false).map(s => s.label));
    const m = params.modeledCount;
    const n = params.novelCount;
    const pickCycle = (pool: string[], count: number, offset = 0) =>
      Array.from({ length: count }, (_, i) => (pool.length ? pool[(offset + i) % pool.length] : ''));
    const modeledStyles = pickCycle(styles, m);
    const novelStyles = pickCycle(styles, n, m);
    // One shuffled queue across both groups keeps angles distinct run-wide;
    // each slot takes the first queued angle its style doesn't conflict with.
    const angleQueue = shuffled(ANGLE_OPTIONS);
    const angleFor = (style: string) => {
      const banned = STYLE_ANGLE_CONFLICTS[style] || [];
      const at = angleQueue.findIndex(angle => banned.indexOf(angle) === -1);
      return angleQueue.splice(at === -1 ? 0 : at, 1)[0] || ANGLE_OPTIONS[0];
    };
    const modeledAngles = modeledStyles.map(angleFor);
    const novelAngles = novelStyles.map(angleFor);
    const headlinePool = shuffled(HEADLINE_ANGLE_OPTIONS);
    const list = (xs: string[]) => xs.map((x, i) => `${i + 1}) ${x}`).join(', ');
    // With every caption style unchecked there is no style to pair an angle
    // with, so the rotation directs angles ALONE rather than emitting slots
    // with an empty style name ("1)  + Lifestyle").
    const pair = (ss: string[], as: string[]) => ss.map((s, i) => `${i + 1}) ${s ? `${s} + ` : ''}${as[i]}`).join(', ');
    const paired = styles.length ? '(style + angle)' : '(angle only — no caption styles are checked)';
    return [
      "THIS RUN'S ROTATION (a fresh random draw every run — follow it exactly; it overrides your own pairing habits):",
      `- The ${m} modeled primary texts use ${paired}: ${pair(modeledStyles, modeledAngles)}. The ${n} novel primary texts use: ${pair(novelStyles, novelAngles)}.`,
      `- The ${m} modeled headlines use angles: ${list(pickCycle(headlinePool, m))}. The ${n} novel headlines use angles: ${list(pickCycle(headlinePool, n, m))}.`,
      '- These are two separate lists: a text written for one list must NEVER also appear in the other.',
    ].join('\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.modeledCount, params.novelCount, params.styles, rotationNonce]);

  const values: Record<string, string> = useMemo(() => ({
    [VAR_PRODUCT]: host?.product || 'the product',
    [VAR_SELLING_POINTS]: params.enabled.selling_points
      ? (host?.sellingPoints || []).filter((_, i) => sellingChecked[i]).map(p => `- ${p}`).join('\n')
      : '',
    [VAR_ANALYSIS]: params.enabled.analysis ? analysisText : '',
    [VAR_WINNING_HEADLINES]: params.enabled.winning_headlines ? winnerLines(winningHeadlines) : '',
    [VAR_WINNING_PRIMARY_TEXTS]: params.enabled.winning_primary_texts ? fencedWinnerBlocks(winningPrimary) : '',
    [VAR_ORIGINAL_HEADLINE]: params.enabled.original ? originalText.headline : '',
    [VAR_ORIGINAL_PRIMARY_TEXT]: params.enabled.original ? originalText.primaryText : '',
    // Full prior primaries prime the model TOWARD their imagery (the
    // pink-elephant effect) — only the opening hook goes in, where repetition
    // actually shows, framed as a positive different-device demand.
    [VAR_PREVIOUS_COPY]: params.enabled.previous_copy && priorItems.some(i => i.checked)
      ? [
        'Headlines already used (do not repeat or lightly rephrase):',
        ...priorItems.filter(i => i.checked && i.kind === 'headline').map(i => `- ${i.text}`),
        'Opening hooks already used — every new primary text must open with a DIFFERENT device and different imagery:',
        ...priorItems.filter(i => i.checked && i.kind === 'primary').map(i => `- "${firstHookLine(i.text)}"`),
      ].join('\n')
      : '',
    [VAR_HEADLINE_RULES]: params.enabled.headline_rules ? headlineRules : '',
    [VAR_STYLE_LIBRARY]: params.enabled.style_library ? `${buildStyleLibrary(params.styles)}\n\n${rotationText}` : '',
    [VAR_FORMAT_RULES]: params.enabled.format_rules ? formatRules : '',
    [VAR_MODELED_COUNT]: String(params.modeledCount),
    [VAR_NOVEL_COUNT]: String(params.novelCount),
    [VAR_EXTRA]: params.enabled.extra ? extra : '',
  }), [host, params, sellingChecked, analysisText, originalText, extra, winningHeadlines, winningPrimary, priorItems, headlineRules, formatRules, rotationText]);

  const assembledPrompt = useMemo(() => composePrompt(template, values), [template, values]);
  const totalTokens = estimateTokens(assembledPrompt);

  // A run that claims to be winners-grounded must not be paid for while the
  // winners are missing or still arriving. Turning both winner cards off is
  // the deliberate way to generate without them.
  const winnersWanted = !!(params.enabled.winning_headlines || params.enabled.winning_primary_texts);
  const winnersBlocked = !!winnersError && winnersWanted;
  const winnersPending = winnersLoading && winnersWanted;
  const retryWinners = () => setWinnersReloadNonce(nonce => nonce + 1);

  const run = async () => {
    setGenerating(true);
    setError(null);
    setWarning(null);
    try {
      const resp = await generate(assembledPrompt, model);
      // Only land on Results once there is a result — during generation the
      // user stays where they set the run up, and a failure keeps them there.
      setTab('results');
      setRotationNonce(nonce => nonce + 1); // next run draws a fresh rotation
      const notes = [
        resp.truncated
          ? 'The model ran out of output room and the response was cut off — options are missing, and anything marked "cut off" stops mid-sentence. Consider generating again.'
          : '',
        // The copy is already paid for; it just is not in this ad's history,
        // so it will be gone when the dialog is reopened.
        resp.historyError
          ? `This copy could NOT be saved to the run history (${resp.historyError}) — copy anything you want to keep before closing the studio.`
          : '',
      ].filter(Boolean);
      if (notes.length) setWarning(notes.join(' '));
      setRawResponse(resp.text);
      if (resp.runId) {
        setViewedRunId(resp.runId);
        setHistory(prev => [
          { runId: resp.runId!, model: resp.model || model, responseText: resp.text, createdAt: new Date().toISOString() },
          ...prev,
        ]);
      }
      // Fresh generations flow straight into the host's slots (winners first)
      // so the user edits from a filled panel. Viewing history does NOT
      // re-apply — only an explicit Generate does.
      if (onApplyAll) {
        const parsed = splitSuggestions(resp.text);
        // Only FINISHED options are written for the user: a mid-sentence
        // fragment auto-filled into the panel is copy they never chose and
        // could publish without noticing. Cut-off options stay on screen,
        // marked, for them to finish and Use deliberately.
        const complete = (options: SuggestionOption[]) => options.filter(o => !o.truncated).map(o => o.text);
        const headlines = complete([...parsed.headlinesModeled, ...parsed.headlinesNovel]);
        const primaries = complete([...parsed.primaryModeled, ...parsed.primaryNovel]);
        if (headlines.length || primaries.length) onApplyAll(headlines.slice(0, maxItems), primaries.slice(0, maxItems));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const suggestions = useMemo(() => (rawResponse ? splitSuggestions(rawResponse) : null), [rawResponse]);

  // The Results tab exists only once there is something to show; deleting the
  // last run (or opening a creative with none) falls back to Generate.
  const hasResults = history.length > 0 || !!rawResponse;
  useEffect(() => {
    if (!hasResults && tab === 'results') setTab('generate');
  }, [hasResults, tab]);

  // Memoized: the lists reset their edit drafts when their options change
  // identity, so these arrays must only change when the response does — an
  // inline literal here would wipe the user's edits on every re-render.
  const withTag = (options: SuggestionOption[], tag: 'winners' | 'novel'): TaggedOption[] =>
    options.map(o => ({ text: o.text, angle: o.angle, style: o.style, truncated: o.truncated, tag }));
  const headlineOptions = useMemo(() => (suggestions
    ? [...withTag(suggestions.headlinesModeled, 'winners'), ...withTag(suggestions.headlinesNovel, 'novel')]
    : []), [suggestions]);
  const primaryOptions = useMemo(() => (suggestions
    ? [...withTag(suggestions.primaryModeled, 'winners'), ...withTag(suggestions.primaryNovel, 'novel')]
    : []), [suggestions]);
  // Derived from the stored response, so a run reopened from history reports
  // its truncation exactly as the run that produced it did.
  const cutOffCount = headlineOptions.filter(o => o.truncated).length + primaryOptions.filter(o => o.truncated).length;

  const cardTokens = (variable: string) => estimateTokens(values[variable] || '');

  const saveTeamDefaults = () => {
    void settingsService.updateOrThrow(STUDIO_SETTINGS_NAMESPACE, {
      [STUDIO_PROMPT_KEY]: template,
      [STUDIO_PARAMS_KEY]: { ...params },
      [STUDIO_HEADLINE_RULES_KEY]: headlineRules,
      [STUDIO_FORMAT_RULES_KEY]: formatRules,
    }).then(() => {
      setTeamSavedFlash(true);
      setTimeout(() => setTeamSavedFlash(false), 2500);
    }).catch((err: any) => {
      setError(err?.response?.data?.error || err?.message || 'Saving the team default failed');
    });
  };

  return (
    <Dialog open={open} onClose={generating ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {title || 'Ad Copy Studio'}
        <Chip size="small" sx={{ ml: 1 }} label={`prompt ~${totalTokens} tokens`} />
        <Button size="small" sx={{ ml: 'auto' }} onClick={() => setSectionSettingsOpen(true)}>
          Section defaults
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 36 }}>
          <Tab value="generate" label="Generate" sx={{ minHeight: 36 }} />
          {hasResults && (
            <Tab value="results" label={history.length ? `Results (${history.length})` : 'Results'} sx={{ minHeight: 36 }} />
          )}
        </Tabs>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {warning && <Alert severity="warning" sx={{ mb: 1.5 }}>{warning}</Alert>}
        {hostLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {/* Both panels stay MOUNTED and hide via CSS — suggestion edit
            drafts and accordion states survive tab switches. */}
        {!hostLoading && (
          <Box sx={{ display: tab === 'generate' ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}>
            {/* ── Step 2: generation ── */}
            <Stack spacing={1.5} sx={{ width: '100%', display: genStep === 'generate' ? 'flex' : 'none' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setGenStep('ingredients')}>
                  Ingredients
                </Button>
                <Typography variant="overline" color="text.secondary">Generation</Typography>
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small" label="Modeled on winners" type="number" value={params.modeledCount}
                  inputProps={{ min: 0, max: 5 }} sx={{ width: 130 }}
                  onChange={e => setParam('modeledCount', Math.max(0, Math.min(5, Number(e.target.value) || 0)))}
                />
                <TextField
                  size="small" label="Novel" type="number" value={params.novelCount}
                  inputProps={{ min: 0, max: 5 }} sx={{ width: 90 }}
                  onChange={e => setParam('novelCount', Math.max(0, Math.min(5, Number(e.target.value) || 0)))}
                />
                <TextField
                  select size="small" label="Model" value={model} sx={{ minWidth: 180 }}
                  onChange={(e) => { setModel(e.target.value); localStorage.setItem(STUDIO_MODEL_STORAGE_KEY, e.target.value); }}
                >
                  {MODEL_OPTIONS.map((m: { value: string; label: string }) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained" onClick={run} disabled={generating || winnersBlocked || winnersPending}
                  startIcon={generating ? <CircularProgress size={14} /> : undefined}
                  sx={{ flex: 1, minWidth: 220 }}
                >
                  {generating ? 'Writing…' : `Generate ${params.modeledCount} modeled + ${params.novelCount} novel`}
                </Button>
              </Stack>
              {winnersBlocked && (
                <Alert
                  severity="error"
                  action={<Button color="inherit" size="small" onClick={retryWinners}>Retry</Button>}
                >
                  {winnersError} This run costs money and would NOT be grounded in our winning copy, so
                  it is blocked. Retry, or switch the two "Winning …" cards off on the Ingredients step
                  to generate deliberately without them.
                </Alert>
              )}
              {!winnersBlocked && winnersPending && (
                <Alert severity="info">Loading the winners this run will be grounded in…</Alert>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Prompt that will be sent (~{totalTokens} tokens) — click it to edit the template behind it
                </Typography>
                <Box
                  onClick={() => setTemplateOpen(o => !o)}
                  sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', maxHeight: 260, overflowY: 'auto', cursor: 'pointer' }}
                >
                  <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.72rem', m: 0 }}>
                    {assembledPrompt}
                  </Typography>
                </Box>
              </Box>
              <Collapse in={templateOpen}>
                <Stack spacing={1}>
                  <TextField
                    fullWidth multiline minRows={10} maxRows={18} value={template}
                    onChange={(e) => { setTemplate(e.target.value); localStorage.setItem(STUDIO_PROMPT_STORAGE_KEY, e.target.value); }}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.72rem' } }}
                    helperText="The template. Sections whose ingredient card is off resolve empty and are pruned from the assembled prompt above, which follows every edit."
                  />
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => {
                      localStorage.removeItem(STUDIO_PROMPT_STORAGE_KEY);
                      setTemplate(DEFAULT_STUDIO_PROMPT);
                    }}>
                      Reset to built-in
                    </Button>
                  </Stack>
                </Stack>
              </Collapse>
            </Stack>

            {/* ── Step 1: ingredients ── */}
            <Stack spacing={1} sx={{ width: '100%', display: genStep === 'ingredients' ? 'flex' : 'none' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
                  Ingredients — everything below is exactly what the prompt gets
                </Typography>
                <Button variant="contained" size="small" endIcon={<ArrowForwardIcon />} onClick={() => setGenStep('generate')}>
                  Generation
                </Button>
              </Stack>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Tabs
                orientation="vertical"
                value={ingredientTab}
                onChange={(_, v) => setIngredientTab(v)}
                sx={{ borderRight: 1, borderColor: 'divider', minWidth: 96, flexShrink: 0 }}
              >
                {STUDIO_SECTION_GROUPS.map(group => {
                  const ids = STUDIO_SECTIONS.filter(section => section.group === group.key);
                  const on = ids.filter(section => params.enabled[section.id] !== false).length;
                  return (
                    <Tab
                      key={group.key} value={group.key}
                      sx={{ minHeight: 44, alignItems: 'flex-start', textAlign: 'left', pl: 2, pr: 1 }}
                      label={`${group.label} ${on}/${ids.length}`}
                    />
                  );
                })}
              </Tabs>
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>

              <Stack spacing={1} sx={{ display: ingredientTab === 'winners' ? 'flex' : 'none' }}>
              {/* How the winner pool is selected — lives with the winner lists it feeds. */}
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  size="small" label="Lookback (days)" type="number" value={params.lookbackDays}
                  inputProps={{ min: 7, max: 180 }} sx={{ width: 120 }}
                  onChange={e => setParam('lookbackDays', Math.max(7, Math.min(180, Number(e.target.value) || 30)))}
                />
                <Tooltip title={params.winnersMode === 'sample'
                  ? 'Sample mode always ranks by True ROAS — the same measure its floor reads'
                  : ''}>
                  <TextField
                    select size="small" label="Rank winners by" value={params.metric} sx={{ width: 150 }}
                    disabled={params.winnersMode === 'sample'}
                    onChange={e => setParam('metric', e.target.value as StudioParams['metric'])}
                  >
                    <MenuItem value="true_roas">True ROAS</MenuItem>
                    <MenuItem value="meta_roas">Meta ROAS</MenuItem>
                    <MenuItem value="spend">Spend</MenuItem>
                  </TextField>
                </Tooltip>
                <TextField
                  size="small" label="Min spend $" type="number" value={params.minSpend}
                  inputProps={{ min: 0 }} sx={{ width: 110 }}
                  onChange={e => setParam('minSpend', Math.max(0, Number(e.target.value) || 0))}
                />
                <TextField
                  size="small" label="Winners each" type="number" value={params.winnersPerType}
                  inputProps={{ min: 1, max: 15 }} sx={{ width: 110 }}
                  onChange={e => setParam('winnersPerType', Math.max(1, Math.min(15, Number(e.target.value) || 6)))}
                />
                <TextField
                  select size="small" label="Winners pick" value={params.winnersMode} sx={{ width: 170 }}
                  onChange={e => setParam('winnersMode', e.target.value as StudioParams['winnersMode'])}
                >
                  <MenuItem value="top">Top by metric</MenuItem>
                  <MenuItem value="sample">Random ≥ floor (top {SAMPLE_POOL_LIMIT} ROAS)</MenuItem>
                </TextField>
                {params.winnersMode === 'sample' && (
                  <TextField
                    size="small" label="ROAS floor" type="number" value={params.roasFloor}
                    inputProps={{ min: 0, step: 0.5 }} sx={{ width: 100 }}
                    onChange={e => setParam('roasFloor', Math.max(0, Number(e.target.value) || 0))}
                  />
                )}
                {params.winnersMode === 'sample' && (
                  <Button
                    size="small" variant="outlined"
                    disabled={winnersLoading || (!winnersPool.current.h.length && !winnersPool.current.p.length)}
                    onClick={() => {
                      setWinningHeadlines(drawSample(winnersPool.current.h, params.winnersPerType));
                      setWinningPrimary(drawSample(winnersPool.current.p, params.winnersPerType));
                    }}
                  >
                    Shuffle winners
                  </Button>
                )}
              </Stack>

              {winnersError && (
                <Alert
                  severity="error"
                  action={<Button color="inherit" size="small" onClick={retryWinners}>Retry</Button>}
                >
                  {winnersError} The lists below are EMPTY because the request failed, not because
                  no winners qualify.
                </Alert>
              )}

              <IngredientCard
                id="winning_headlines" label={`Winning headlines (${params.lookbackDays}d)`}
                enabled={params.enabled.winning_headlines} onToggle={on => setEnabled('winning_headlines', on)}
                tokens={cardTokens(VAR_WINNING_HEADLINES)}
              >
                {winnersLoading ? <CircularProgress size={16} /> : (
                  <CheckList
                    items={winningHeadlines}
                    onToggle={i => setWinningHeadlines(rows => rows.map((r, j) => (j === i ? { ...r, checked: !r.checked } : r)))}
                    meta={i => `${ratio(rowRoas(winningHeadlines[i]))} ROAS · ${money(winningHeadlines[i].spend)}`}
                  />
                )}
              </IngredientCard>

              <IngredientCard
                id="winning_primary_texts" label={`Winning primary texts (${params.lookbackDays}d)`}
                enabled={params.enabled.winning_primary_texts} onToggle={on => setEnabled('winning_primary_texts', on)}
                tokens={cardTokens(VAR_WINNING_PRIMARY_TEXTS)}
              >
                {winnersLoading ? <CircularProgress size={16} /> : (
                  <CheckList
                    items={winningPrimary}
                    onToggle={i => setWinningPrimary(rows => rows.map((r, j) => (j === i ? { ...r, checked: !r.checked } : r)))}
                    meta={i => `${ratio(rowRoas(winningPrimary[i]))} ROAS · ${money(winningPrimary[i].spend)}`}
                  />
                )}
              </IngredientCard>

              </Stack>

              <Stack spacing={1} sx={{ display: ingredientTab === 'creative' ? 'flex' : 'none' }}>
              <IngredientCard
                id="original" label="Original ad's copy"
                enabled={params.enabled.original} onToggle={on => setEnabled('original', on)}
                tokens={estimateTokens(`${values[VAR_ORIGINAL_HEADLINE]}${values[VAR_ORIGINAL_PRIMARY_TEXT]}`)}
              >
                <Stack spacing={0.75}>
                  <TextField
                    size="small" label="Headline" value={originalText.headline}
                    onChange={e => setOriginalText(prev => ({ ...prev, headline: e.target.value }))}
                  />
                  <TextField
                    size="small" label="Primary text" multiline maxRows={4} value={originalText.primaryText}
                    onChange={e => setOriginalText(prev => ({ ...prev, primaryText: e.target.value }))}
                  />
                </Stack>
              </IngredientCard>

              <IngredientCard
                id="analysis" label="Why the original worked (analysis)"
                enabled={params.enabled.analysis} onToggle={on => setEnabled('analysis', on)}
                tokens={cardTokens(VAR_ANALYSIS)}
              >
                <TextField
                  size="small" fullWidth multiline maxRows={8} value={analysisText}
                  onChange={e => setAnalysisText(e.target.value)}
                />
              </IngredientCard>

              <IngredientCard
                id="selling_points" label="Selling points"
                enabled={params.enabled.selling_points} onToggle={on => setEnabled('selling_points', on)}
                tokens={cardTokens(VAR_SELLING_POINTS)}
              >
                <CheckList
                  items={(host?.sellingPoints || []).map((text, i) => ({ text, checked: !!sellingChecked[i] }))}
                  onToggle={i => setSellingChecked(prev => prev.map((c, j) => (j === i ? !c : c)))}
                />
              </IngredientCard>


              </Stack>

              <Stack spacing={1} sx={{ display: ingredientTab === 'history' ? 'flex' : 'none' }}>
              <IngredientCard
                id="previous_copy" label={`Previously generated — this product (${priorItems.length})`}
                enabled={params.enabled.previous_copy} onToggle={on => setEnabled('previous_copy', on)}
                tokens={cardTokens(VAR_PREVIOUS_COPY)}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Fed to the model as "do NOT repeat these". The 5 most recent of each kind are pre-checked.
                </Typography>
                {(['headline', 'primary'] as const).map(kind => (
                  <Box key={kind} sx={{ mb: kind === 'headline' ? 1 : 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {kind === 'headline' ? 'Headlines' : 'Primary texts'}
                    </Typography>
                    <Box sx={{ maxHeight: 190, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Table size="small">
                        <TableBody>
                          {priorItems.map((item, i) => (item.kind !== kind ? null : (
                            // eslint-disable-next-line react/no-array-index-key
                            <TableRow
                              key={i} hover sx={{ cursor: 'pointer' }}
                              onClick={() => setPriorItems(prev => prev.map((it, j) => (j === i ? { ...it, checked: !it.checked } : it)))}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox size="small" checked={item.checked} sx={{ p: 0.25 }} />
                              </TableCell>
                              <TableCell sx={{ px: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{item.text}</Typography>
                              </TableCell>
                            </TableRow>
                          )))}
                          {!priorItems.some(i => i.kind === kind) && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography variant="caption" color="text.disabled">None yet.</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                ))}
              </IngredientCard>

              </Stack>

              <Stack spacing={1} sx={{ display: ingredientTab === 'rules' ? 'flex' : 'none' }}>
              <IngredientCard
                id="extra" label="Extra instructions"
                enabled={params.enabled.extra} onToggle={on => setEnabled('extra', on)}
                tokens={cardTokens(VAR_EXTRA)}
              >
                <TextField
                  size="small" fullWidth multiline maxRows={4} value={extra}
                  placeholder="e.g. lean into the summer heat angle"
                  onChange={e => setExtra(e.target.value)}
                />
              </IngredientCard>

              <IngredientCard
                id="headline_rules" label="Headline rules"
                enabled={params.enabled.headline_rules} onToggle={on => setEnabled('headline_rules', on)}
                tokens={cardTokens(VAR_HEADLINE_RULES)}
              >
                <TextField
                  size="small" fullWidth multiline maxRows={8} value={headlineRules}
                  InputProps={{ sx: { fontSize: '0.75rem' } }}
                  onChange={(e) => { setHeadlineRules(e.target.value); localStorage.setItem(STUDIO_HEADLINE_RULES_STORAGE_KEY, e.target.value); }}
                />
              </IngredientCard>

              <IngredientCard
                id="style_library" label="Caption styles"
                enabled={params.enabled.style_library} onToggle={on => setEnabled('style_library', on)}
                tokens={cardTokens(VAR_STYLE_LIBRARY)}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Checked styles rotate across the options — fewer checked = fewer tokens and a directed format mix.
                </Typography>
                {!STYLE_LIBRARY.some(style => params.styles[style.key] !== false) && (
                  <Alert severity="info" sx={{ mb: 0.5, py: 0 }}>
                    No styles checked — the rotation will direct angles only, and the caption formats
                    leave the prompt entirely. Switch this card off to drop the section instead.
                  </Alert>
                )}
                <CheckList
                  items={STYLE_LIBRARY.map(style => ({ text: style.label, checked: params.styles[style.key] !== false }))}
                  onToggle={i => setParams(prev => {
                    const key = STYLE_LIBRARY[i].key;
                    const next = { ...prev, styles: { ...prev.styles, [key]: prev.styles[key] === false } };
                    saveParams(next);
                    return next;
                  })}
                  meta={i => STYLE_LIBRARY[i].line.replace(/^[^(]*/, '')}
                />
              </IngredientCard>

              <IngredientCard
                id="format_rules" label="Caption format rules"
                enabled={params.enabled.format_rules} onToggle={on => setEnabled('format_rules', on)}
                tokens={cardTokens(VAR_FORMAT_RULES)}
              >
                <TextField
                  size="small" fullWidth multiline maxRows={10} value={formatRules}
                  InputProps={{ sx: { fontSize: '0.75rem' } }}
                  onChange={(e) => { setFormatRules(e.target.value); localStorage.setItem(STUDIO_FORMAT_RULES_STORAGE_KEY, e.target.value); }}
                />
              </IngredientCard>
              </Stack>

              </Stack>
              </Box>
            </Stack>

          </Box>
        )}
        {!hostLoading && (
          <Stack spacing={1.5} sx={{ display: tab === 'results' ? 'flex' : 'none' }}>
              {history.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Runs for this ad ({history.length})
                  </Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {history.map((h) => (
                      <Tooltip key={h.runId} title={h.model || ''}>
                        <Chip
                          size="small"
                          variant={h.runId === viewedRunId ? 'filled' : 'outlined'}
                          color={h.runId === viewedRunId ? 'primary' : 'default'}
                          label={new Date(h.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          onClick={() => {
                            if (!h.responseText) return;
                            setRawResponse(h.responseText);
                            setViewedRunId(h.runId);
                          }}
                          onDelete={deleteRun ? () => setDeleteTarget(h) : undefined}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}

              {generating && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
                  <CircularProgress />
                  <Typography variant="caption" color="text.secondary">Writing ad copy…</Typography>
                </Box>
              )}
              {!generating && !suggestions && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No runs yet — set up the ingredients on the Generate tab and hit Generate.
                </Typography>
              )}
              {!generating && suggestions && (
                <>
                  {suggestions.truncated && (
                    <Alert severity="warning">
                      This response was cut off before the model finished, so options are missing
                      {cutOffCount > 0
                        ? ` and ${cutOffCount} of the ones below stop mid-sentence (marked "cut off"). Those are never written into the ad-copy panel automatically — finish one and hit Use, or generate again.`
                        : '. Generate again for the full set.'}
                    </Alert>
                  )}
                  <SuggestionList
                    title="Headlines"
                    options={headlineOptions}
                    onUse={onUseHeadline}
                    onFull={text => setReplaceTarget({ kind: 'headline', text })}
                    onTextsChange={onHeadlineTexts}
                  />
                  <SuggestionList
                    title="Primary text"
                    options={primaryOptions}
                    onUse={onUsePrimaryText}
                    onFull={text => setReplaceTarget({ kind: 'primaryText', text })}
                    onTextsChange={onPrimaryTexts}
                  />
                  {suggestions.unparsed && (
                    <Alert severity="warning">
                      This response didn't come back in the expected format, so it could NOT be split
                      into suggestion cards — nothing here was applied to the ad-copy panel. Its full
                      raw text is below; copy anything usable by hand, or generate again.
                    </Alert>
                  )}
                  <Accordion disableGutters defaultExpanded={!!suggestions.unparsed} key={viewedRunId || 'latest'}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="caption">Full response</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', m: 0 }}>
                        {rawResponse}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {tab === 'results' && !generating && suggestions && !suggestions.unparsed && onApplyAll
          && (screenHeadlines.length > 0 || screenPrimaries.length > 0) && (
          <Button variant="outlined" onClick={() => setReplaceAllOpen(true)}>
            Use All Headlines And Text
          </Button>
        )}
        <Button onClick={onClose} disabled={generating}>Close</Button>
      </DialogActions>

      <Dialog open={!!deleteTarget} onClose={deletingRun ? undefined : () => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete this run?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The run from {deleteTarget ? new Date(deleteTarget.createdAt).toLocaleString() : ''} and its
            suggestions will be permanently removed from this ad's history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deletingRun}>Cancel</Button>
          <Button
            color="error" variant="contained" disabled={deletingRun}
            startIcon={deletingRun ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={() => {
              if (!deleteTarget || !deleteRun) return;
              setDeletingRun(true);
              void deleteRun(deleteTarget.runId).then(() => {
                const remaining = history.filter(h => h.runId !== deleteTarget.runId);
                setHistory(remaining);
                if (viewedRunId === deleteTarget.runId) {
                  setRawResponse(remaining[0]?.responseText || null);
                  setViewedRunId(remaining[0]?.runId || null);
                }
                setDeleteTarget(null);
              }).catch((err: any) => {
                setError(err?.response?.data?.error || err?.message || 'Delete failed');
                setDeleteTarget(null);
              }).finally(() => setDeletingRun(false));
            }}
          >
            {deletingRun ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* All slots full — pick which one this text replaces. */}
      <Dialog open={!!replaceTarget} onClose={() => setReplaceTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>All {maxItems} {replaceTarget?.kind === 'headline' ? 'headline' : 'caption'} slots are full</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>Adding:</Typography>
          <Box sx={{ p: 1, mb: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="body2">{replaceTarget?.text}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>Choose which one it replaces:</Typography>
          <Stack spacing={0.75}>
            {(replaceTarget && getSlots ? getSlots(replaceTarget.kind) : []).map((slot, i) => (
              <Button
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                variant="outlined"
                sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}
                onClick={() => {
                  if (replaceTarget && replaceSlot) replaceSlot(replaceTarget.kind, i, replaceTarget.text);
                  setReplaceTarget(null);
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{i + 1}. {slot || '(empty)'}</Typography>
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceTarget(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Replace-all confirmation. */}
      <Dialog open={replaceAllOpen} onClose={() => setReplaceAllOpen(false)} maxWidth="xs">
        <DialogTitle>Replace all copy in the panel?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Every headline and caption in the ad-copy panel will be replaced with the
            {' '}{applyHeadlines.length} headline{applyHeadlines.length === 1 ? '' : 's'} and
            {' '}{applyPrimaries.length} primary text{applyPrimaries.length === 1 ? '' : 's'} shown here
            (including your edits). This can't be undone.
          </Typography>
          {(screenHeadlines.length > applyHeadlines.length || screenPrimaries.length > applyPrimaries.length) && (
            <Typography variant="body2" sx={{ mt: 1 }} color="warning.main">
              The panel holds {maxItems} of each, so only the first {maxItems} shown will be applied —
              the rest stay here for you to Use individually.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceAllOpen(false)}>Cancel</Button>
          <Button
            color="error" variant="contained"
            onClick={() => {
              onApplyAll?.(applyHeadlines, applyPrimaries, { save: true });
              setReplaceAllOpen(false);
              // The panel now holds the copy — the studio's job is done.
              onClose();
            }}
          >
            Replace all
          </Button>
        </DialogActions>
      </Dialog>
      {/* Section defaults: the SAME persisted enabled-state the cards use —
          flipping here changes this run AND every future open (and rides
          along in "Save as team default"). */}
      <Dialog open={sectionSettingsOpen} onClose={() => setSectionSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Section defaults</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Off = the section never enters the prompt until switched back on. Changes apply to this
            browser right away; "Save as team default" below shares the current template, tunables
            and these toggles with everyone.
          </Typography>
          {STUDIO_SECTION_GROUPS.map(group => (
            <Box key={group.key} sx={{ mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary">{group.label}</Typography>
              {STUDIO_SECTIONS.filter(section => section.group === group.key).map(section => (
                <Stack key={section.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{section.label}</Typography>
                  <Switch
                    size="small"
                    checked={params.enabled[section.id] !== false}
                    onChange={e => setEnabled(section.id, e.target.checked)}
                  />
                </Stack>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Tooltip title={teamDefaultsError
            ? "Team defaults couldn't be loaded — saving now could overwrite them"
            : ''}>
            <Box component="span" sx={{ mr: 'auto' }}>
              <Button size="small" disabled={teamDefaultsError} onClick={saveTeamDefaults}>
                {teamSavedFlash ? 'Saved ✓' : 'Save as team default'}
              </Button>
            </Box>
          </Tooltip>
          <Button onClick={() => setSectionSettingsOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
